/**
 * Постраничный вывод реестров Сметчика и открытие записи по адресу.
 *
 * Реестры получали с сервера первые 50 записей без признака «есть ещё», и
 * запись старше пятидесятой открыть было нельзя (замечание F04 экспертизы
 * 03.11.2026). Теперь сервер отдаёт страницу и `has_more`, экран листает
 * страницы, а выбранная запись живёт в адресе (`?id=…`) — её можно открыть
 * ссылкой, даже если она не на первой странице.
 */
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@shared/ui/Button';

/** Записей на странице реестра — столько же сервер отдаёт по умолчанию. */
export const REGISTRY_PAGE_SIZE = 50;

export interface RegistryPage {
  limit: number;
  offset: number;
  setOffset: (offset: number) => void;
}

/** Текущая страница реестра (с начала — самые новые записи). */
export function useRegistryPage(): RegistryPage {
  const [offset, setOffset] = useState(0);
  return { limit: REGISTRY_PAGE_SIZE, offset, setOffset };
}

/**
 * Выбранная запись реестра в адресе страницы (`?id=…`) — вместо состояния
 * компонента: ссылка на запись открывает её сразу, кнопка «Назад» браузера
 * возвращает к прежней.
 */
export function useSelectedRecordParam(): [string | null, (id: string | null) => void] {
  const [params, setParams] = useSearchParams();
  const selected = params.get('id');
  const setSelected = useCallback(
    (id: string | null) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('id', id);
        else next.delete('id');
        return next;
      });
    },
    [setParams],
  );
  return [selected, setSelected];
}

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface RegistryPagerProps {
  /** Как назвать записи в счётчике: «Проверок», «Сессий», «Пакетов». */
  label: string;
  page: RegistryPage;
  /** Сколько записей на текущей странице. */
  shown: number;
  hasMore: boolean | undefined;
  /** Страница ещё грузится — не перелистывать пустую назад раньше времени. */
  fetching?: boolean;
}

/**
 * Счётчик и листание реестра. Всё уместилось на одну страницу — прежний
 * счётчик «Проверок: N»; иначе — номера записей и «Предыдущие / Следующие».
 */
export function RegistryPager({ label, page, shown, hasMore, fetching }: RegistryPagerProps) {
  const { limit, offset, setOffset } = page;
  // Последнюю запись страницы удалили — страница опустела, шагаем назад.
  useEffect(() => {
    if (!fetching && shown === 0 && offset > 0) setOffset(Math.max(0, offset - limit));
  }, [fetching, shown, offset, limit, setOffset]);

  if (offset === 0 && !hasMore) {
    return (
      <Bar>
        {label}: {shown}
      </Bar>
    );
  }
  return (
    <Bar>
      <span>
        {shown > 0 ? `Записи ${offset + 1}–${offset + shown}` : 'Записей на странице нет'}
        {hasMore ? ', есть ещё' : ''}
      </span>
      <Button
        type="button"
        $variant="ghost"
        $size="sm"
        disabled={offset === 0}
        onClick={() => setOffset(Math.max(0, offset - limit))}
      >
        ← Новее
      </Button>
      <Button
        type="button"
        $variant="ghost"
        $size="sm"
        disabled={!hasMore}
        onClick={() => setOffset(offset + limit)}
      >
        Старее →
      </Button>
    </Bar>
  );
}
